import type {
  AnalysisResult,
  SignalData,
  SignalResultBundle,
} from "../runtime/runtime";

export type { SignalResultBundle } from "../runtime/runtime";

export const SIGNAL_LAYOUT_MODES = [
  "overlay",
  "vertical",
  "horizontal",
] as const;

export type SignalLayoutMode = (typeof SIGNAL_LAYOUT_MODES)[number];

// Plotly trims ordinary whitespace before showing its editable title placeholder.
const INVISIBLE_AXIS_TITLE = "\u200b";

export function bundleSignalResults(
  signalIds: string[],
  resultLists: AnalysisResult[][],
): SignalResultBundle[] {
  return resultLists.map((results, index) => ({
    signalId: signalIds[index],
    results,
  }));
}

export interface SignalAxisAssignment {
  signalId: string;
  xRef: string;
  yRef: string;
  xLayoutKey: string;
  yLayoutKey: string;
}

export interface SignalAxisGroup {
  id: string;
  signalIds: string[];
}

export interface SignalPlotLayout {
  effectiveMode: SignalLayoutMode;
  assignments: SignalAxisAssignment[];
  axisGroups: SignalAxisGroup[];
  axes: Record<string, Record<string, unknown>>;
  minWidth?: number;
  minHeight?: number;
}

export function normalizeSignalLayoutMode(
  value: string | null | undefined,
): SignalLayoutMode {
  return SIGNAL_LAYOUT_MODES.includes(value as SignalLayoutMode)
    ? (value as SignalLayoutMode)
    : "overlay";
}

export function formatSignalAxis(label: string, unit: string): string {
  return unit ? `${label} (${unit})` : label;
}

function synchronizationKey(signal: SignalData): string | null {
  // Unit symbols are case-sensitive (for example, "s" and "S" are
  // scientifically different), while human-readable labels are not.
  const unit = signal.xunit.trim();
  if (unit) return `unit:${unit}`;
  const label = signal.xlabel.trim().toLowerCase();
  return label ? `label:${label}` : null;
}

export function haveCompatibleSignalXAxes(
  first: SignalData,
  second: SignalData,
): boolean {
  const firstKey = synchronizationKey(first);
  return firstKey !== null && firstKey === synchronizationKey(second);
}

function axisRef(prefix: "x" | "y", index: number): string {
  return index === 0 ? prefix : `${prefix}${index + 1}`;
}

function axisLayoutKey(prefix: "xaxis" | "yaxis", index: number): string {
  return index === 0 ? prefix : `${prefix}${index + 1}`;
}

function domains(count: number): Array<[number, number]> {
  if (count <= 1) return [[0, 1]];
  const gap = Math.min(0.04, 0.2 / (count - 1));
  const size = (1 - gap * (count - 1)) / count;
  return Array.from({ length: count }, (_, index) => {
    const start = index * (size + gap);
    return [start, start + size];
  });
}

export function normalizeSignalAxisGroups(
  signals: SignalData[],
  groups?: readonly SignalAxisGroup[],
): SignalAxisGroup[] {
  const knownSignalIds = new Set(signals.map((signal) => signal.id));
  const assignedSignalIds = new Set<string>();
  const usedGroupIds = new Set<string>();
  const normalized: SignalAxisGroup[] = [];

  for (const group of groups ?? []) {
    const signalIds = group.signalIds.filter((signalId) => {
      if (!knownSignalIds.has(signalId) || assignedSignalIds.has(signalId)) {
        return false;
      }
      assignedSignalIds.add(signalId);
      return true;
    });
    if (signalIds.length === 0) continue;
    let id = group.id.trim();
    if (!id || usedGroupIds.has(id)) id = `axis:${signalIds[0]}`;
    while (usedGroupIds.has(id)) id = `${id}:next`;
    usedGroupIds.add(id);
    normalized.push({ id, signalIds });
  }

  for (const signal of signals) {
    if (assignedSignalIds.has(signal.id)) continue;
    let id = `axis:${signal.id}`;
    while (usedGroupIds.has(id)) id = `${id}:next`;
    usedGroupIds.add(id);
    normalized.push({ id, signalIds: [signal.id] });
  }
  return normalized;
}

function groupSynchronizationKey(signals: SignalData[]): string | null {
  const firstKey = signals[0] ? synchronizationKey(signals[0]) : null;
  if (firstKey === null) return null;
  return signals.every((signal) => synchronizationKey(signal) === firstKey)
    ? firstKey
    : null;
}

export function buildSignalPlotLayout(
  signals: SignalData[],
  requestedMode: SignalLayoutMode,
  requestedGroups?: readonly SignalAxisGroup[],
): SignalPlotLayout {
  const effectiveMode =
    signals.length <= 1 ? "overlay" : normalizeSignalLayoutMode(requestedMode);
  const axisGroups = normalizeSignalAxisGroups(signals, requestedGroups);

  if (effectiveMode === "overlay") {
    const primary = signals[0];
    return {
      effectiveMode,
      assignments: signals.map((signal) => ({
        signalId: signal.id,
        xRef: "x",
        yRef: "y",
        xLayoutKey: "xaxis",
        yLayoutKey: "yaxis",
      })),
      axisGroups,
      axes: primary
        ? {
            xaxis: {
              title: {
                text: formatSignalAxis(primary.xlabel || "X", primary.xunit),
              },
              automargin: true,
            },
            yaxis: {
              title: {
                text: formatSignalAxis(primary.ylabel || "Y", primary.yunit),
              },
              automargin: true,
            },
          }
        : {},
    };
  }

  const signalById = new Map(signals.map((signal) => [signal.id, signal]));
  const groupIndexBySignalId = new Map<string, number>();
  axisGroups.forEach((group, groupIndex) => {
    group.signalIds.forEach((signalId) => {
      groupIndexBySignalId.set(signalId, groupIndex);
    });
  });
  const assignments = signals.map((signal) => {
    const groupIndex = groupIndexBySignalId.get(signal.id) ?? 0;
    return {
      signalId: signal.id,
      xRef: axisRef("x", groupIndex),
      yRef: axisRef("y", groupIndex),
      xLayoutKey: axisLayoutKey("xaxis", groupIndex),
      yLayoutKey: axisLayoutKey("yaxis", groupIndex),
    };
  });
  const splitDomains = domains(axisGroups.length);
  const syncKeys = axisGroups.map((group) =>
    groupSynchronizationKey(
      group.signalIds.flatMap((signalId) => {
        const signal = signalById.get(signalId);
        return signal ? [signal] : [];
      }),
    ),
  );
  const lastIndexBySyncKey = new Map<string, number>();
  syncKeys.forEach((key, index) => {
    if (key !== null) lastIndexBySyncKey.set(key, index);
  });
  const firstAxisBySyncKey = new Map<string, string>();
  const axes: Record<string, Record<string, unknown>> = {};

  axisGroups.forEach((group, index) => {
    const signal = signalById.get(group.signalIds[0]);
    if (!signal) return;
    const xRef = axisRef("x", index);
    const yRef = axisRef("y", index);
    const xLayoutKey = axisLayoutKey("xaxis", index);
    const yLayoutKey = axisLayoutKey("yaxis", index);
    const syncKey = syncKeys[index];
    const firstMatchingAxis =
      syncKey === null ? undefined : firstAxisBySyncKey.get(syncKey);
    if (syncKey !== null && firstMatchingAxis === undefined) {
      firstAxisBySyncKey.set(syncKey, xRef);
    }
    const showXLabels =
      effectiveMode === "horizontal" ||
      syncKey === null ||
      lastIndexBySyncKey.get(syncKey) === index;
    const xDomain =
      effectiveMode === "horizontal" ? splitDomains[index] : [0, 1];
    const yDomain =
      effectiveMode === "vertical"
        ? splitDomains[axisGroups.length - index - 1]
        : [0, 1];

    axes[xLayoutKey] = {
      domain: xDomain,
      anchor: yRef,
      ...(firstMatchingAxis ? { matches: firstMatchingAxis } : {}),
      showticklabels: showXLabels,
      title: {
        text: showXLabels
          ? formatSignalAxis(signal.xlabel || "X", signal.xunit)
          : INVISIBLE_AXIS_TITLE,
      },
      automargin: true,
    };
    axes[yLayoutKey] = {
      domain: yDomain,
      anchor: xRef,
      title: {
        text: formatSignalAxis(signal.ylabel || "Y", signal.yunit),
      },
      automargin: true,
    };
  });

  return {
    effectiveMode,
    assignments,
    axisGroups,
    axes,
    minWidth:
      effectiveMode === "horizontal"
        ? Math.max(720, axisGroups.length * 360)
        : undefined,
    minHeight:
      effectiveMode === "vertical"
        ? Math.max(440, axisGroups.length * 220)
        : undefined,
  };
}
